import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, PencilSimple, Trash, SignOut, Eye, EyeSlash, GoogleDriveLogo, CheckCircle, Warning, Plug, PlugsConnected, MagnifyingGlass, ArrowsClockwise, ChartLineUp, InstagramLogo } from "@phosphor-icons/react";
import { InstagramTile } from "@/components/InstagramFeed";
import { toast, Toaster } from "sonner";

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [posts, setPosts] = useState([]);
    const [busy, setBusy] = useState(true);
    const [drive, setDrive] = useState({ connected: false, loading: true });
    const [driveBusy, setDriveBusy] = useState(false);
    const [indexNow, setIndexNow] = useState({ loading: true, endpoints: [], key: "", key_location: "", site: "", environment: null, is_production: null, request_host: "" });
    const [indexNowBusy, setIndexNowBusy] = useState(false);
    const [insta, setInsta] = useState({ loading: true, connected: false, configured: false, cache_count: 0 });
    const [instaPosts, setInstaPosts] = useState([]);
    const [instaBusy, setInstaBusy] = useState(false);

    const loadPosts = useCallback(async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/posts");
            setPosts(data);
        } catch {
            toast.error("Failed to load posts");
        } finally { setBusy(false); }
    }, []);

    const loadDriveStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/drive/status");
            setDrive({ ...data, loading: false });
        } catch {
            setDrive({ connected: false, loading: false });
        }
    }, []);

    const loadIndexNowStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/indexnow/status");
            setIndexNow({ ...data, loading: false });
        } catch {
            setIndexNow(s => ({ ...s, loading: false }));
        }
    }, []);

    const pingIndexNow = async () => {
        setIndexNowBusy(true);
        try {
            const { data } = await api.post("/admin/indexnow/ping");
            toast.success(`Pinged search engines with ${data.url_count} URL${data.url_count === 1 ? "" : "s"}`);
            // Backend pings are fire-and-forget; wait briefly for results to land.
            setTimeout(() => { loadIndexNowStatus(); setIndexNowBusy(false); }, 2500);
        } catch {
            toast.error("Failed to trigger IndexNow ping");
            setIndexNowBusy(false);
        }
    };

    const loadInstagram = useCallback(async () => {
        try {
            const [statusResp, postsResp] = await Promise.all([
                api.get("/admin/instagram/status"),
                api.get("/instagram/posts").catch(() => ({ data: { posts: [] } })),
            ]);
            setInsta({ ...statusResp.data, loading: false });
            setInstaPosts(postsResp.data?.posts || []);
        } catch {
            setInsta(s => ({ ...s, loading: false }));
        }
    }, []);

    const connectInstagram = async () => {
        setInstaBusy(true);
        try {
            const { data } = await api.get("/admin/instagram/connect");
            if (data.authorize_url) window.location.href = data.authorize_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Instagram is not configured yet");
            setInstaBusy(false);
        }
    };

    const manualConnectInstagram = async (accessToken) => {
        const token = (accessToken || "").trim();
        if (!token) {
            toast.error("Paste the access token first");
            return;
        }
        setInstaBusy(true);
        try {
            const { data } = await api.post("/admin/instagram/manual-connect", { access_token: token });
            toast.success(`Connected as @${data.username}`);
            await loadInstagram();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Token rejected by Meta");
        } finally {
            setInstaBusy(false);
        }
    };

    const refreshInstagram = async () => {
        setInstaBusy(true);
        try {
            const { data } = await api.post("/admin/instagram/refresh");
            toast.success(`Fetched ${data.count} post${data.count === 1 ? "" : "s"}`);
            await loadInstagram();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Refresh failed");
        } finally {
            setInstaBusy(false);
        }
    };

    const toggleAutoPublish = async (nextEnabled) => {
        try {
            const { data } = await api.post("/admin/instagram/auto-publish", { enabled: nextEnabled });
            setInsta(s => ({ ...s, auto_publish: data.enabled }));
            toast.success(data.enabled ? "Auto-share ON — every publish will post to Instagram" : "Auto-share OFF");
        } catch {
            toast.error("Failed to update auto-share");
        }
    };

    const disconnectInstagram = async () => {
        if (!window.confirm("Disconnect Instagram? Cached posts will be cleared.")) return;
        setInstaBusy(true);
        try {
            await api.post("/admin/instagram/disconnect");
            toast.success("Instagram disconnected");
            await loadInstagram();
        } catch {
            toast.error("Disconnect failed");
        } finally {
            setInstaBusy(false);
        }
    };

    const sharePost = async (post) => {
        if (!insta.connected) {
            toast.error("Connect Instagram first");
            return;
        }
        if (!post.cover_image) {
            toast.error("Add a cover image before sharing to Instagram");
            return;
        }
        const alreadyShared = !!post.instagram_media_id;
        if (alreadyShared && !window.confirm("This post has already been shared. Share it again?")) return;
        const t = toast.loading("Publishing to Instagram… this can take up to a minute.");
        // Optimistically mark this row as pending so the UI updates immediately;
        // the poll below will replace it with the real status.
        setPosts(prev => prev.map(p => p.id === post.id ? { ...p, instagram_share_status: "pending", instagram_share_error: null } : p));
        try {
            await api.post(`/admin/posts/${post.id}/share-instagram`);
        } catch (e) {
            toast.dismiss(t);
            toast.error(e?.response?.data?.detail || "Instagram publish failed");
            setPosts(prev => prev.map(p => p.id === post.id ? { ...p, instagram_share_status: null } : p));
            return;
        }
        // Poll for the outcome (backend does the publish in the background).
        const started = Date.now();
        const maxMs = 180_000; // 3 minutes
        const pollOnce = async () => {
            try {
                const { data } = await api.get("/admin/posts");
                setPosts(data);
                const updated = data.find(p => p.id === post.id);
                const status = updated?.instagram_share_status;
                if (status === "success") {
                    toast.dismiss(t);
                    toast.success(`Posted to Instagram (id ${updated.instagram_media_id})`);
                    loadInstagram();
                    return true;
                }
                if (status === "failed") {
                    toast.dismiss(t);
                    toast.error(updated?.instagram_share_error || "Instagram publish failed");
                    return true;
                }
            } catch {
                // Ignore transient errors during polling.
            }
            return false;
        };
        const tick = async () => {
            if (await pollOnce()) return;
            if (Date.now() - started > maxMs) {
                toast.dismiss(t);
                toast.error("Still publishing — check back in a minute.");
                return;
            }
            setTimeout(tick, 3000);
        };
        setTimeout(tick, 3000);
    };

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate("/login", { replace: true });
            return;
        }
        if (!user.is_admin) {
            navigate("/login", { replace: true });
            return;
        }
        loadPosts();
        loadDriveStatus();
        loadIndexNowStatus();
        loadInstagram();
    }, [user, loading, navigate, loadPosts, loadDriveStatus, loadIndexNowStatus, loadInstagram]);

    // Surface success / error after returning from the OAuth callback redirect.
    useEffect(() => {
        const status = params.get("drive");
        if (!status) return;
        if (status === "connected") {
            toast.success("Google Drive connected");
            loadDriveStatus();
        } else if (status === "error") {
            const reason = params.get("reason") || "unknown";
            const messages = {
                missing_scope:
                    "Google didn't grant the Drive permission. On the consent screen, make sure the “See, edit, create, and delete only the specific Google Drive files you use with this app” box is CHECKED, then try Connect again.",
                no_refresh_token:
                    "Google didn't return a refresh token. Revoke this app's access at https://myaccount.google.com/permissions and click Connect again.",
                bad_state:
                    "OAuth session expired. Click Connect again.",
                unknown:
                    "Drive connection failed. Try Connect again.",
            };
            toast.error(messages[reason] || messages.unknown, { duration: 15000 });
        }
        setParams({}, { replace: true });
    }, [params, setParams, loadDriveStatus]);

    const handleDelete = async (post) => {
        if (!window.confirm(`Delete "${post.title}"?`)) return;
        try {
            await api.delete(`/posts/${post.id}`);
            toast.success("Post deleted");
            setPosts(prev => prev.filter(p => p.id !== post.id));
        } catch {
            toast.error("Failed to delete");
        }
    };

    const togglePublished = async (post) => {
        try {
            await api.put(`/posts/${post.id}`, { published: !post.published });
            toast.success(post.published ? "Unpublished" : "Published");
            setPosts(prev => prev.map(p => p.id === post.id ? { ...p, published: !p.published } : p));
        } catch {
            toast.error("Failed to update");
        }
    };

    const connectDrive = async () => {
        setDriveBusy(true);
        try {
            const { data } = await api.get("/admin/drive/connect");
            window.location.href = data.authorization_url;
        } catch {
            toast.error("Failed to start Drive connect flow");
            setDriveBusy(false);
        }
    };

    const disconnectDrive = async () => {
        if (!window.confirm("Disconnect Google Drive? You'll need to reconnect before uploading new images. Existing images remain on your Drive.")) return;
        setDriveBusy(true);
        try {
            await api.post("/admin/drive/disconnect");
            toast.success("Disconnected");
            setDrive({ connected: false, loading: false });
        } catch {
            toast.error("Failed to disconnect");
        } finally {
            setDriveBusy(false);
        }
    };

    if (loading || !user) {
        return <div className="min-h-screen grid place-items-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Checking access...</div>;
    }

    return (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12" data-testid="admin-dashboard">
            <Toaster position="top-right" theme="dark" />
            <div className="border-b border-zinc-800 pb-8 mb-10 flex flex-wrap items-end justify-between gap-6">
                <div>
                    <div className="overline mb-3">// Control Room</div>
                    <h1 className="font-display text-white text-5xl md:text-7xl leading-[0.9] tracking-tight">
                        DASHBOARD<span className="text-[#FFD000]">.</span>
                    </h1>
                    <div className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
                        Signed in: {user.username}
                    </div>
                </div>
                <div className="flex gap-3">
                    <Link to="/admin/analytics" className="btn-secondary inline-flex items-center gap-2" data-testid="analytics-link">
                        <ChartLineUp size={16} weight="bold" /> Analytics
                    </Link>
                    <Link to="/admin/social" className="btn-secondary inline-flex items-center gap-2" data-testid="social-manager-link">
                        <InstagramLogo size={16} weight="bold" /> Social
                    </Link>
                    <Link to="/admin/new" className="btn-primary inline-flex items-center gap-2" data-testid="new-post-button">
                        <Plus size={16} weight="bold" /> New Post
                    </Link>
                    <button onClick={async () => { await logout(); navigate("/"); }} className="btn-secondary inline-flex items-center gap-2" data-testid="admin-logout-button">
                        <SignOut size={16} weight="bold" /> Logout
                    </button>
                </div>
            </div>

            <DriveCard
                drive={drive}
                onConnect={connectDrive}
                onDisconnect={disconnectDrive}
                busy={driveBusy}
            />

            <IndexNowCard
                data={indexNow}
                onPing={pingIndexNow}
                busy={indexNowBusy}
            />

            <InstagramCard
                data={insta}
                posts={instaPosts}
                busy={instaBusy}
                onConnect={connectInstagram}
                onManualConnect={manualConnectInstagram}
                onRefresh={refreshInstagram}
                onDisconnect={disconnectInstagram}
                onToggleAutoPublish={toggleAutoPublish}
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800 mb-10" data-testid="admin-stats">
                <Stat label="Total" value={posts.length} />
                <Stat label="News" value={posts.filter(p => p.category === "news").length} />
                <Stat label="Reviews" value={posts.filter(p => p.category === "reviews").length} />
                <Stat label="Videos" value={posts.filter(p => p.category === "videos").length} />
            </div>

            {renderPostsArea(busy, posts, handleDelete, togglePublished, sharePost, insta.connected)}
        </div>
    );
}

function DriveCard({ drive, onConnect, onDisconnect, busy }) {
    const connected = drive.connected;
    return (
        <div
            className={`mb-10 border ${connected ? "border-[#FFD000]/30" : "border-zinc-800"} bg-[#0A0A0A] p-6 flex flex-wrap items-center gap-6 justify-between`}
            data-testid="drive-status-card"
        >
            <div className="flex items-center gap-4">
                <div className={`w-12 h-12 grid place-items-center border ${connected ? "border-[#FFD000] text-[#FFD000]" : "border-zinc-700 text-zinc-500"}`}>
                    <GoogleDriveLogo size={22} weight="bold" />
                </div>
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                        Image Storage
                    </div>
                    <div className="font-display text-white text-2xl flex items-center gap-2">
                        Google Drive
                        {drive.loading ? null : connected ? (
                            <CheckCircle size={18} weight="fill" className="text-[#FFD000]" data-testid="drive-connected-badge" />
                        ) : (
                            <Warning size={18} weight="fill" className="text-orange-400" data-testid="drive-disconnected-badge" />
                        )}
                    </div>
                    <p className="text-xs text-zinc-400 mt-1 max-w-xl leading-relaxed">
                        {connected
                            ? "Uploads from the editor are saved to your Google Drive folder “Tizona Tech Uploads” and embedded into articles as public links."
                            : "Connect your Google account so all hero + inline images upload to your Google Drive. Required before publishing posts with uploaded images."}
                    </p>
                </div>
            </div>
            <div className="flex gap-2">
                {connected ? (
                    <button
                        type="button"
                        onClick={onDisconnect}
                        disabled={busy || drive.loading}
                        data-testid="drive-disconnect-button"
                        className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        <Plug size={14} weight="bold" /> Disconnect
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={onConnect}
                        disabled={busy || drive.loading}
                        data-testid="drive-connect-button"
                        className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        <PlugsConnected size={14} weight="bold" /> {busy ? "Opening…" : "Connect Google Drive"}
                    </button>
                )}
            </div>
        </div>
    );
}

function endpointLabel(url) {
    try {
        const host = new URL(url).host;
        if (host.includes("bing")) return "Bing (direct)";
        if (host.includes("yandex")) return "Yandex (direct)";
        if (host.includes("indexnow")) return "IndexNow hub (Bing + Yandex + Naver + Seznam)";
        return host;
    } catch {
        return url;
    }
}

function timeAgo(iso) {
    if (!iso) return "Never";
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

function IndexNowCard({ data, onPing, busy }) {
    const endpoints = data.endpoints || [];
    const allOk = endpoints.length > 0 && endpoints.every(e => e.ok === true);
    const anyPinged = endpoints.some(e => e.status_code !== null);
    const isPreview = data.is_production === false;
    return (
        <div
            className={`mb-10 border ${allOk ? "border-[#FFD000]/30" : "border-zinc-800"} bg-[#0A0A0A]`}
            data-testid="indexnow-status-card"
        >
            {isPreview && !data.loading && (
                <div className="px-6 py-3 bg-sky-950/40 border-b border-sky-900 text-sky-200 text-[11px] font-mono uppercase tracking-[0.18em] leading-relaxed" data-testid="indexnow-env-banner">
                    Preview environment — pings from this pod will fail with HTTP 403 because Bing only trusts the verified production host ({data.site?.replace(/^https?:\/\//, "")}).
                    Real submissions from production are working; check Bing Webmaster Tools → IndexNow to see live counts.
                </div>
            )}
            <div className="p-6 flex flex-wrap items-center gap-6 justify-between border-b border-zinc-800">
                <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 grid place-items-center border ${allOk ? "border-[#FFD000] text-[#FFD000]" : "border-zinc-700 text-zinc-500"}`}>
                        <MagnifyingGlass size={22} weight="bold" />
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1 flex items-center gap-2">
                            Search Engine Indexing
                            {data.environment && (
                                <span
                                    className={`px-1.5 py-0.5 text-[9px] tracking-[0.2em] ${
                                        data.is_production
                                            ? "bg-[#FFD000] text-black"
                                            : "bg-sky-900 text-sky-200 border border-sky-800"
                                    }`}
                                    data-testid="indexnow-env-pill"
                                >
                                    {data.environment}
                                </span>
                            )}
                        </div>
                        <div className="font-display text-white text-2xl">IndexNow</div>
                        <p className="text-xs text-zinc-400 mt-1 max-w-xl leading-relaxed">
                            Bing, Yandex, and friends are auto-notified whenever you publish, update, or delete a post.
                            Google discovers changes via your sitemap.
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onPing}
                    disabled={busy || data.loading}
                    data-testid="indexnow-ping-button"
                    className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60"
                >
                    <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
                    {busy ? "Pinging…" : "Re-ping now"}
                </button>
            </div>
            <div className="divide-y divide-zinc-800" data-testid="indexnow-endpoint-list">
                {data.loading ? (
                    <div className="px-6 py-5 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                        Loading status…
                    </div>
                ) : endpoints.length === 0 ? (
                    <div className="px-6 py-5 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                        No endpoints configured.
                    </div>
                ) : endpoints.map(ep => (
                    <EndpointRow key={ep.endpoint} ep={ep} />
                ))}
            </div>
            {!data.loading && !anyPinged && (
                <div className="px-6 py-3 border-t border-zinc-800 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    No pings yet since last backend restart. Publish a post or hit &quot;Re-ping now&quot;.
                </div>
            )}
        </div>
    );
}

function EndpointRow({ ep }) {
    const status = ep.status_code;
    let badgeClass = "text-zinc-400 bg-zinc-900 border border-zinc-800";
    let badgeText = "—";
    if (ep.ok === true) {
        badgeClass = "text-black bg-[#FFD000]";
        badgeText = `HTTP ${status} OK`;
    } else if (ep.ok === false) {
        badgeClass = "text-red-200 bg-red-950 border border-red-900";
        badgeText = status ? `HTTP ${status}` : "Error";
    } else if (ep.message && ep.message !== "No ping yet") {
        badgeText = ep.message;
    } else {
        badgeText = "Awaiting first ping";
    }
    return (
        <div
            className="px-6 py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start"
            data-testid={`indexnow-endpoint-${new URL(ep.endpoint).host}`}
        >
            <div className="md:col-span-5">
                <div className="font-display text-white text-lg leading-tight">{endpointLabel(ep.endpoint)}</div>
                <div className="font-mono text-[10px] text-zinc-500 break-all mt-1">{ep.endpoint}</div>
            </div>
            <div className="md:col-span-3">
                <span className={`inline-block font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${badgeClass}`}>
                    {badgeText}
                </span>
                {ep.message && ep.ok === false && (
                    <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed line-clamp-3 break-words">
                        {ep.message}
                    </div>
                )}
            </div>
            <div className="md:col-span-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {timeAgo(ep.pinged_at)}
            </div>
            <div className="md:col-span-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 text-right">
                {ep.url_count ? `${ep.url_count} URL${ep.url_count === 1 ? "" : "s"}` : "—"}
            </div>
        </div>
    );
}

function renderPostsArea(busy, posts, handleDelete, togglePublished, sharePost, igConnected) {
    if (busy) {
        return (
            <div className="text-center py-24 border border-zinc-800 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
                Loading posts...
            </div>
        );
    }
    return renderPostsList(posts, handleDelete, togglePublished, sharePost, igConnected);
}

function InstagramCard({ data, posts, busy, onConnect, onManualConnect, onRefresh, onDisconnect, onToggleAutoPublish }) {
    const configured = data.configured;
    const connected = data.connected;
    const autoOn = !!data.auto_publish;
    const expiresDays = data.expires_at
        ? Math.max(0, Math.ceil((new Date(data.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : null;
    const [manualToken, setManualToken] = React.useState("");
    const [showManual, setShowManual] = React.useState(false);
    return (
        <div className="mb-10 border border-zinc-800 bg-[#0A0A0A]" data-testid="instagram-admin-card">
            <div className="p-6 flex flex-wrap items-center gap-6 justify-between border-b border-zinc-800">
                <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 grid place-items-center border ${connected ? "border-[#FFD000] text-[#FFD000]" : "border-zinc-700 text-zinc-400"}`}>
                        <InstagramLogo size={22} weight="bold" />
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1 flex items-center gap-2">
                            Home Page Widget
                            {connected ? (
                                <span className="px-1.5 py-0.5 text-[9px] tracking-[0.2em] bg-[#FFD000] text-black">CONNECTED</span>
                            ) : (
                                <span className="px-1.5 py-0.5 text-[9px] tracking-[0.2em] bg-zinc-800 text-zinc-400 border border-zinc-700">
                                    {configured ? "NOT CONNECTED" : "NOT CONFIGURED"}
                                </span>
                            )}
                        </div>
                        <div className="font-display text-white text-2xl">
                            Instagram Feed{data.username && <span className="text-zinc-500"> · @{data.username}</span>}
                        </div>
                        {connected ? (
                            <p className="text-xs text-zinc-400 mt-1 max-w-xl leading-relaxed">
                                {data.cache_count || posts.length} posts cached · auto-refresh hourly ·
                                token expires in {expiresDays} day{expiresDays === 1 ? "" : "s"} (auto-renewed before it does)
                            </p>
                        ) : configured ? (
                            <p className="text-xs text-zinc-400 mt-1 max-w-xl leading-relaxed">
                                Click <b className="text-white">Connect Instagram</b> and authorize @tizonatech. We&apos;ll fetch your latest 12 posts and keep them in sync automatically.
                            </p>
                        ) : (
                            <p className="text-xs text-orange-300 mt-1 max-w-xl leading-relaxed">
                                Set <code className="text-[#FFD000]">INSTAGRAM_APP_ID</code>, <code className="text-[#FFD000]">INSTAGRAM_APP_SECRET</code>, and <code className="text-[#FFD000]">INSTAGRAM_REDIRECT_URI</code> in backend/.env. See the setup steps I gave in chat.
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {!connected ? (
                        <button
                            type="button"
                            onClick={onConnect}
                            disabled={busy || !configured}
                            data-testid="instagram-connect-button"
                            className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            <Plug size={14} weight="bold" /> Connect Instagram
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={onRefresh}
                                disabled={busy}
                                data-testid="instagram-refresh-button"
                                className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60"
                            >
                                <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
                                Refresh now
                            </button>
                            <button
                                type="button"
                                onClick={onDisconnect}
                                disabled={busy}
                                data-testid="instagram-disconnect-button"
                                className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60 hover:!text-red-300 hover:!border-red-900"
                            >
                                Disconnect
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="p-6">
                {!connected && configured && (
                    <div className="mb-6 border border-zinc-800 p-4" data-testid="instagram-manual-connect">
                        <button
                            type="button"
                            onClick={() => setShowManual((s) => !s)}
                            className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-[#FFD000]"
                            data-testid="instagram-manual-toggle"
                        >
                            {showManual ? "▾ Hide manual token setup" : "▸ Or paste a token from Meta dashboard"}
                        </button>
                        {showManual && (
                            <div className="mt-4 space-y-3 text-sm">
                                <p className="text-zinc-400 leading-relaxed">
                                    If OAuth throws <em>&quot;Invalid platform app&quot;</em> (usual when the Meta app is in Development mode), skip it: in the Meta dashboard go to <b className="text-white">Instagram → API setup → 1. Generate access tokens</b>, click <b className="text-white">Generate token</b>, copy the long token, and paste it below.
                                </p>
                                <textarea
                                    value={manualToken}
                                    onChange={(e) => setManualToken(e.target.value)}
                                    placeholder="Paste the long-lived access token…"
                                    rows={3}
                                    className="w-full bg-black border border-zinc-800 text-white font-mono text-xs p-3 focus:border-[#FFD000] focus:outline-none"
                                    data-testid="instagram-manual-token-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => onManualConnect(manualToken)}
                                    disabled={busy || !manualToken.trim()}
                                    className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                                    data-testid="instagram-manual-connect-submit"
                                >
                                    <Plug size={14} weight="bold" /> Connect with pasted token
                                </button>
                            </div>
                        )}
                    </div>
                )}
                {connected && (
                    <label
                        className="mb-6 flex items-start gap-3 p-4 border border-zinc-800 hover:border-zinc-700 cursor-pointer transition-colors"
                        data-testid="instagram-autoshare-toggle-label"
                    >
                        <input
                            type="checkbox"
                            checked={autoOn}
                            onChange={(e) => onToggleAutoPublish(e.target.checked)}
                            data-testid="instagram-autoshare-toggle"
                            className="mt-1 w-4 h-4 accent-[#FFD000]"
                        />
                        <div>
                            <div className="font-display text-white text-lg leading-tight">
                                Auto-share new posts to Instagram
                                {autoOn && <span className="ml-3 px-1.5 py-0.5 text-[9px] tracking-[0.2em] bg-[#FFD000] text-black align-middle">ON</span>}
                            </div>
                            <div className="font-mono text-[11px] text-zinc-500 mt-1 leading-relaxed">
                                When a post is published (and has an HTTPS cover image), a card is auto-posted to Instagram with the title, excerpt, and article link. Skips posts already shared. Can also share individual posts manually via the article list.
                            </div>
                        </div>
                    </label>
                )}
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
                    // Live preview — this is exactly how it renders on the home page
                </div>
                {posts.length === 0 ? (
                    <div className="border border-dashed border-zinc-800 p-10 text-center">
                        <InstagramLogo size={32} weight="light" className="text-zinc-600 mx-auto mb-3" />
                        <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                            {connected ? "No posts fetched yet — try Refresh" : "Connect Instagram to see your posts here"}
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="instagram-preview-grid">
                        {posts.slice(0, 12).map((p) => (
                            <InstagramTile key={p.id} post={p} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function renderPostsList(posts, handleDelete, togglePublished, sharePost, igConnected) {
    if (posts.length === 0) {
        return (
            <div className="text-center py-24 border border-zinc-800">
                <div className="font-display text-3xl text-white mb-2">No posts yet.</div>
                <Link to="/admin/new" className="text-[#FFD000] font-mono text-xs uppercase tracking-[0.25em]">+ Create your first post</Link>
            </div>
        );
    }
    return (
        <div className="border border-zinc-800" data-testid="admin-posts-table">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-zinc-800 bg-[#121212] font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <div className="col-span-6">Title</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-3 text-right">Actions</div>
            </div>
            {posts.map(p => (
                <PostRow
                    key={p.id}
                    post={p}
                    onDelete={handleDelete}
                    onTogglePublished={togglePublished}
                    onShare={sharePost}
                    igConnected={igConnected}
                />
            ))}
        </div>
    );
}

function PostRow({ post: p, onDelete, onTogglePublished, onShare, igConnected }) {
    const shared = !!p.instagram_media_id;
    const shareError = p.instagram_share_error;
    const publishing = p.instagram_share_status === "pending";
    const canShare = igConnected && p.published && !!p.cover_image && !publishing;
    let shareTitle = "Share to Instagram";
    if (!igConnected) shareTitle = "Connect Instagram first";
    else if (!p.published) shareTitle = "Publish before sharing";
    else if (!p.cover_image) shareTitle = "Add a cover image first";
    else if (publishing) shareTitle = "Publishing to Instagram…";
    else if (shared) shareTitle = "Share again to Instagram (already shared once)";

    return (
        <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-zinc-800 last:border-b-0 items-center hover:bg-zinc-900/30" data-testid={`admin-row-${p.id}`}>
            <div className="col-span-6">
                <Link to={`/post/${p.slug}`} className="text-white hover:text-[#FFD000] font-medium">
                    {p.title}
                </Link>
                <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3">
                    <span>{new Date(p.created_at).toLocaleDateString()}</span>
                    {publishing && (
                        <span className="text-[#FFD000] font-mono uppercase tracking-[0.2em] text-[9px] animate-pulse" data-testid={`ig-publishing-${p.id}`}>
                            · IG publishing…
                        </span>
                    )}
                    {shared && !publishing && (
                        <span className="text-[#FFD000] font-mono uppercase tracking-[0.2em] text-[9px]" title={`Shared: ${p.instagram_shared_at}`}>
                            · IG ✓
                        </span>
                    )}
                    {shareError && !shared && !publishing && (
                        <span className="text-red-400 font-mono uppercase tracking-[0.2em] text-[9px]" title={shareError}>
                            · IG error
                        </span>
                    )}
                </div>
            </div>
            <div className="col-span-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFD000] bg-zinc-900 border border-zinc-800 px-2 py-1">
                    {p.category}
                </span>
            </div>
            <div className="col-span-1">
                <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${
                    p.published ? "text-black bg-[#FFD000]" : "text-zinc-400 bg-zinc-900 border border-zinc-800"
                }`}>
                    {p.published ? "Published" : "Draft"}
                </span>
            </div>
            <div className="col-span-3 flex justify-end gap-1">
                <button
                    onClick={() => onShare(p)}
                    disabled={!canShare}
                    className={`p-2 ${shared ? "text-[#FFD000]" : "text-zinc-400"} hover:text-[#FFD000] disabled:opacity-30 disabled:hover:text-zinc-400 disabled:cursor-not-allowed`}
                    title={shareTitle}
                    data-testid={`share-instagram-${p.id}`}
                >
                    <InstagramLogo size={16} weight={shared ? "fill" : "bold"} />
                </button>
                <button onClick={() => onTogglePublished(p)} className="p-2 text-zinc-400 hover:text-[#FFD000]" title="Toggle publish" data-testid={`toggle-${p.id}`}>
                    {p.published ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                </button>
                <Link to={`/admin/edit/${p.id}`} className="p-2 text-zinc-400 hover:text-[#FFD000]" title="Edit" data-testid={`edit-${p.id}`}>
                    <PencilSimple size={16} weight="bold" />
                </Link>
                <button onClick={() => onDelete(p)} className="p-2 text-zinc-400 hover:text-red-400" title="Delete" data-testid={`delete-${p.id}`}>
                    <Trash size={16} weight="bold" />
                </button>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-[#0A0A0A] p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
            <div className="font-display text-5xl text-white mt-2">{value}</div>
        </div>
    );
}
